/**
 * ContentWarning Component
 * 
 * Displays a dismissible warning for potentially intense or triggering content.
 * Can be configured for different warning types (intense, edge, trauma-related).
 */

import React, { useState } from 'react';
import { AlertTriangle, X, Shield, Heart } from 'lucide-react';

// Warning type configurations
const WARNING_TYPES = {
    intense: {
        icon: AlertTriangle,
        title: 'Intense Content',
        iconColor: 'text-amber-400',
        borderColor: 'border-amber-500/30',
        bgColor: 'bg-amber-950/30',
        description: 'This practice involves elements that some may find intense or emotionally challenging.'
    },
    edgeplay: {
        icon: Shield,
        title: 'Edge Play Warning',
        iconColor: 'text-red-400',
        borderColor: 'border-red-500/30',
        bgColor: 'bg-red-950/30',
        description: 'This is considered edge play with elevated physical or psychological risks.'
    },
    trauma: {
        icon: Heart,
        title: 'Sensitive Topic',
        iconColor: 'text-pink-400',
        borderColor: 'border-pink-500/30',
        bgColor: 'bg-pink-950/30',
        description: 'This topic may be triggering for those with trauma-related experiences.'
    }
};

function ContentWarning({
    type = 'intense',
    customMessage = null,
    showDismiss = true,
    onDismiss = null,
    className = ''
}) {
    const [isDismissed, setIsDismissed] = useState(false);

    if (isDismissed) return null;

    const config = WARNING_TYPES[type] || WARNING_TYPES.intense;
    const Icon = config.icon;

    const handleDismiss = () => {
        setIsDismissed(true);
        if (onDismiss) onDismiss();
    };

    return (
        <div
            className={`rounded-xl p-4 ${config.borderColor} ${config.bgColor} border relative ${className}`}
            role="alert"
            aria-live="polite"
        >
            <div className="flex items-start gap-3">
                <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} aria-hidden="true" />

                <div className="flex-1">
                    <h3 className={`font-fantasy text-sm font-semibold ${config.iconColor} tracking-wide mb-1`}>
                        {config.title}
                    </h3>
                    <p className="text-cream/70 text-sm leading-relaxed">
                        {customMessage || config.description}
                    </p>
                </div>

                {showDismiss && (
                    <button
                        onClick={handleDismiss}
                        className="flex-shrink-0 p-1 text-cream/40 hover:text-cream/70 transition-colors"
                        aria-label="Dismiss warning"
                    >
                        <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                )}
            </div>

            {/* Call to action for resources */}
            <div className="mt-3 pt-3 border-t border-white/5">
                <a
                    href="#safety-resources"
                    className="text-xs text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors"
                >
                    Need support? View mental health resources →
                </a>
            </div>
        </div>
    );
}

export default ContentWarning;
